import { apiClient } from '@services/api-client';

// ===== IP WHITELIST TYPES =====

export interface IpWhitelistEntry {
  id: string;
  tenantId: string;
  ipAddress: string;
  description?: string;
  createdBy?: string;
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IpRestrictionSettings {
  tenantId: string;
  isEnabled: boolean;
  enforceForAll: boolean;
  exemptRoles: string[];
  allowedCountries: string[];
  blockedCountries: string[];
  logFailedAttempts: boolean;
  notifyOnViolation: boolean;
  notificationEmails: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateIpWhitelistDto {
  ipAddress: string;
  description?: string;
  expiresAt?: string;
  isActive?: boolean;
}

export interface UpdateIpWhitelistDto {
  description?: string;
  expiresAt?: string;
  isActive?: boolean;
}

export interface UpdateIpRestrictionSettingsDto {
  isEnabled?: boolean;
  enforceForAll?: boolean;
  exemptRoles?: string[];
  allowedCountries?: string[];
  blockedCountries?: string[];
  logFailedAttempts?: boolean;
  notifyOnViolation?: boolean;
  notificationEmails?: string[];
}

export interface IpViolationLog {
  id: string;
  ipAddress: string;
  userAgent?: string;
  metadata: {
    path?: string;
    method?: string;
    timestamp: string;
  };
  createdAt: string;
}

// ===== SAML SSO TYPES =====

export interface SamlConfiguration {
  id: string;
  tenantId: string;
  isEnabled: boolean;
  idpEntityId: string;
  idpSsoUrl: string;
  idpSloUrl?: string;
  hasCertificate: boolean;
  spEntityId: string;
  spAcsUrl: string;
  hasSpCertificate: boolean;
  signAuthnRequest: boolean;
  wantAssertionsSigned: boolean;
  wantMessagesSigned: boolean;
  signatureAlgorithm: string;
  attributeMapping: Record<string, string>;
  defaultRole: string;
  autoProvisionUsers: boolean;
  allowIdpInitiated: boolean;
  forceAuthn: boolean;
  allowedClockSkew: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSamlConfigurationDto {
  idpEntityId: string;
  idpSsoUrl: string;
  idpSloUrl?: string;
  idpCertificate: string;
  isEnabled?: boolean;
  signAuthnRequest?: boolean;
  wantAssertionsSigned?: boolean;
  wantMessagesSigned?: boolean;
  signatureAlgorithm?: 'sha256' | 'sha384' | 'sha512';
  attributeMapping?: Record<string, string>;
  defaultRole?: string;
  autoProvisionUsers?: boolean;
  allowIdpInitiated?: boolean;
  forceAuthn?: boolean;
  allowedClockSkew?: number;
}

export interface UpdateSamlConfigurationDto extends Partial<CreateSamlConfigurationDto> {}

export interface SsoSession {
  id: string;
  tenantId: string;
  userId: string;
  samlSessionIndex?: string;
  samlNameId?: string;
  ipAddress?: string;
  userAgent?: string;
  expiresAt: string;
  isActive: boolean;
  createdAt: string;
}

class SecurityService {
  // apiClient's baseURL already ends in /api, and the BFF mounts these at
  // `ip-restrictions` and `sso/saml` — there is no `security/*` controller.
  private readonly ipBasePath = '/ip-restrictions';
  private readonly samlBasePath = '/sso/saml';

  // ===== IP WHITELIST =====

  async getIpRestrictionSettings(): Promise<IpRestrictionSettings> {
    const response = await apiClient.get<IpRestrictionSettings>(
      `${this.ipBasePath}/settings`,
    );
    return response.data;
  }

  async updateIpRestrictionSettings(
    data: UpdateIpRestrictionSettingsDto,
  ): Promise<IpRestrictionSettings> {
    const response = await apiClient.put<IpRestrictionSettings>(
      `${this.ipBasePath}/settings`,
      data,
    );
    return response.data;
  }

  async getIpWhitelist(activeOnly = false): Promise<IpWhitelistEntry[]> {
    const response = await apiClient.get<IpWhitelistEntry[]>(
      `${this.ipBasePath}/whitelist`,
      { params: { activeOnly } },
    );
    return response.data;
  }

  async createIpWhitelistEntry(data: CreateIpWhitelistDto): Promise<IpWhitelistEntry> {
    const response = await apiClient.post<IpWhitelistEntry>(
      `${this.ipBasePath}/whitelist`,
      data,
    );
    return response.data;
  }

  async updateIpWhitelistEntry(
    id: string,
    data: UpdateIpWhitelistDto,
  ): Promise<IpWhitelistEntry> {
    const response = await apiClient.put<IpWhitelistEntry>(
      `${this.ipBasePath}/whitelist/${id}`,
      data,
    );
    return response.data;
  }

  async deleteIpWhitelistEntry(id: string): Promise<void> {
    await apiClient.delete(`${this.ipBasePath}/whitelist/${id}`);
  }

  async toggleIpWhitelistEntry(id: string, isActive: boolean): Promise<IpWhitelistEntry> {
    const response = await apiClient.put<IpWhitelistEntry>(
      `${this.ipBasePath}/whitelist/${id}/toggle`,
      null,
      { params: { active: isActive } },
    );
    return response.data;
  }

  async getIpViolationLogs(options?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: IpViolationLog[]; total: number }> {
    const response = await apiClient.get(`${this.ipBasePath}/violations`, {
      params: options,
    });
    return response.data;
  }

  async checkIp(ip: string): Promise<{ ip: string; isAllowed: boolean }> {
    const response = await apiClient.get(`${this.ipBasePath}/check/${ip}`);
    return response.data;
  }

  // ===== SAML SSO =====

  async getSamlConfiguration(): Promise<SamlConfiguration | { configured: false }> {
    const response = await apiClient.get(`${this.samlBasePath}`);
    return response.data;
  }

  async createSamlConfiguration(
    data: CreateSamlConfigurationDto,
  ): Promise<SamlConfiguration> {
    const response = await apiClient.post<SamlConfiguration>(
      `${this.samlBasePath}`,
      data,
    );
    return response.data;
  }

  async updateSamlConfiguration(
    data: UpdateSamlConfigurationDto,
  ): Promise<SamlConfiguration> {
    const response = await apiClient.put<SamlConfiguration>(
      `${this.samlBasePath}`,
      data,
    );
    return response.data;
  }

  async deleteSamlConfiguration(): Promise<void> {
    await apiClient.delete(`${this.samlBasePath}`);
  }

  async toggleSamlEnabled(enabled: boolean): Promise<SamlConfiguration> {
    const response = await apiClient.put<SamlConfiguration>(
      `${this.samlBasePath}/toggle`,
      null,
      { params: { enabled } },
    );
    return response.data;
  }

  async getSamlMetadata(): Promise<{ entityId: string; acsUrl: string; metadataXml: string }> {
    const response = await apiClient.get(`${this.samlBasePath}/metadata/json`);
    return response.data;
  }

  async generateSpKeyPair(): Promise<{ certificate: string; message: string }> {
    const response = await apiClient.post(`${this.samlBasePath}/generate-keypair`);
    return response.data;
  }

  async getSsoSessions(options?: {
    userId?: string;
    activeOnly?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ sessions: SsoSession[]; total: number }> {
    const response = await apiClient.get(`${this.samlBasePath}/sessions`, {
      params: options,
    });
    return response.data;
  }

  async terminateSsoSession(sessionId: string): Promise<void> {
    await apiClient.delete(`${this.samlBasePath}/sessions/${sessionId}`);
  }

  async terminateAllUserSsoSessions(userId: string): Promise<void> {
    await apiClient.delete(`${this.samlBasePath}/sessions/user/${userId}`);
  }

  // ===== SSO LOGIN =====

  getSamlLoginUrl(returnTo?: string): string {
    const baseUrl = '/auth/saml/login';
    return returnTo ? `${baseUrl}?returnTo=${encodeURIComponent(returnTo)}` : baseUrl;
  }

  async getSsoStatus(): Promise<{ configured: boolean; enabled: boolean; forceAuthn: boolean }> {
    const response = await apiClient.get(`${this.samlBasePath}/status`);
    return response.data;
  }
}

export const securityService = new SecurityService();
